import {
  Code2, Network, Database, Workflow, LineChart, Settings2,
  Truck, Factory, HeartPulse, Store,
} from 'lucide-react';

export const LOGO_URL = "https://customer-assets.emergentagent.com/job_5a3546be-0753-4f44-afc9-3ac0ecff8a74/artifacts/ee7ufu4d_ChatGPT%20Image%20Mar%204%2C%202026%2C%2004_40_28%20PM.png";
export const LOGO_SMALL = "https://customer-assets.emergentagent.com/job_9979a9aa-b963-4578-b070-131e8996775c/artifacts/f5sufog6_426c0771-cdbe-4ae6-9d1a-0340a5656588.jpg";

// ─────────────────────────────────────────────────────────────────────
// BBMI-OAI (Brandmeldinstallatie Digitaal Logboek) — sister product
// built in a separate Emergent workspace. Once that app is deployed,
// fill BBMI_APP_DOMAIN with the bare hostname (no protocol, no trailing
// slash, e.g. "bbmi-oai.example.com" or "app.bbmi-oai.nl"). The landing
// page auto-renders real screenshots + an "Open the app" CTA from this
// single value. When empty, the section gracefully falls back to the
// iconographic Flame tile.
//
// BBMI_PLAY_STORE_URL becomes active once the Android build is approved
// in Google Play. Leave empty until the listing is live; the Play
// badge stays hidden until set.
// ─────────────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────
// IMPORTANT ASSET POLICY (added 2026-06-01)
// ─────────────────────────────────────────────────────────────────────
// All product screenshots used on the landing page MUST live under
// /app/frontend/public/marketing/<product>/ and be referenced by a
// LOCAL path like "/marketing/bbmi/dashboard.jpg".
//
// DO NOT hot-link images from:
//   • Other Emergent preview URLs ("*.preview.emergentagent.com")
//     — these pods sleep after inactivity and 404 on idle endpoints.
//   • customer-assets.emergentagent.com paths uploaded in chat
//     — these are also not guaranteed long-term.
//
// Hot-linked images caused all four BBMI screenshots to go AWOL when
// the BBMI-OAI app removed its `/api/legal/screenshot/*` endpoint.
// Hosting screenshots locally guarantees the marketing site stays
// presentable even if a sister app changes its API surface.
// ─────────────────────────────────────────────────────────────────────

export const BBMI_APP_DOMAIN = 'bmi-logboek.preview.emergentagent.com';
export const BBMI_PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.bbmi.oai';
// Local files — copied into /public/marketing/bbmi/ so the landing
// page never depends on the BBMI-OAI sister app's API surface staying
// stable (it disappeared once already, hence this policy).
export const BBMI_SCREENSHOTS = [
  { src: '/marketing/bbmi/signin.jpg',     labelEn: 'Multilingual sign in',   labelNl: 'Meertalige inlog' },
  { src: '/marketing/bbmi/onboarding.jpg', labelEn: 'Region & standard pick', labelNl: 'Regio & norm-keuze' },
  { src: '/marketing/bbmi/buildings.jpg',  labelEn: 'Buildings & installations', labelNl: 'Gebouwen & installaties' },
  { src: '/marketing/bbmi/insights.jpg',   labelEn: 'AI Operational Intelligence', labelNl: 'AI Operationeel Inzicht' },
];

// ─────────────────────────────────────────────────────────────────────
// FlowCrew — third product in the Kaizen Kanri portfolio. Mobile-first
// workforce planning app for SME warehouses. Brand accent: amber
// #FFB800 on a dark industrial background (used inside the card to
// distinguish it from the teal Kaizen Kanri / BBMI-OAI siblings).
//
// Set FLOWCREW_APP_DOMAIN to the bare hostname (no protocol) once the
// app is deployed. While empty, the "Open the app" button is hidden
// and a "Coming soon" pill takes its place. Same pattern for
// FLOWCREW_PLAY_STORE_URL once the Play Store listing is approved.
// ─────────────────────────────────────────────────────────────────────
export const FLOWCREW_APP_DOMAIN = 'crew-dashboard-beta.preview.emergentagent.com';
export const FLOWCREW_PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.flow_crew';
export const FLOWCREW_PLAY_STORE_LIVE = true; // live on Google Play (com.flow_crew)
export const FLOWCREW_ACCENT = '#FFB800';
export const FLOWCREW_SCREENSHOTS = [
  { src: '/marketing/flowcrew/signin.jpg',  labelEn: 'Sign in · Kanriva™ account',      labelNl: 'Inloggen · Kanriva™-account' },
  { src: '/marketing/flowcrew/planner.jpg', labelEn: 'Shift Planner · AI suggest',   labelNl: 'Shift Planner · AI-suggesties' },
  { src: '/marketing/flowcrew/reports.jpg', labelEn: 'Reports & Excel export',       labelNl: 'Rapporten & Excel-export' },
  { src: '/marketing/flowcrew/more.jpg',    labelEn: 'Workers, calendar & settings', labelNl: 'Werknemers, kalender & beheer' },
];

// Kanriva™ product screenshots — captured from a fictional demo tenant
// ("Nordwind Logistics BV": invented names, departments, resource codes
// and volumes). Local files only, per the asset policy at the top.
// Reset/refresh the KANRI records with:
//   cd /app/backend && python3 scripts/seed_kanri_demo.py
export const KANRIVA_SCREENSHOTS = [
  { src: '/marketing/kanriva/kanri-today.jpg',   labelEn: 'KANRI Method · today’s condition', labelNl: 'KANRI-methode · conditie vandaag' },
  { src: '/marketing/kanriva/kanri-know.jpg',    labelEn: 'KANRI · condition board (K)',      labelNl: 'KANRI · conditiebord (K)' },
  { src: '/marketing/kanriva/kanri-respond.jpg', labelEn: 'KANRI · response ladder (R)',      labelNl: 'KANRI · responstrap (R)' },
  { src: '/marketing/kanriva/kanri-metrics.jpg', labelEn: 'KANRI · control-behaviour metrics', labelNl: 'KANRI · beheersgedrag-metrics' },
  { src: '/marketing/kanriva/planning-grid.jpg', labelEn: 'Week planning · shift grid',  labelNl: 'Weekplanning · dienstenraster' },
  { src: '/marketing/kanriva/dashboard.jpg',     labelEn: 'Operations dashboard',        labelNl: 'Operations-dashboard' },
  { src: '/marketing/kanriva/capacity.jpg',      labelEn: 'Workload demand vs coverage', labelNl: 'Werklast vs bezetting' },
  { src: '/marketing/kanriva/kpi-analytics.jpg', labelEn: 'KPI analytics',               labelNl: 'KPI-analyses' },
];
export const KANRIVA_HERO_SHOT = '/marketing/kanriva/kanri-today.jpg';

// Shown over any screenshot once it is zoomed open — every shot on the
// marketing site comes from the fictional demo tenant, never a customer.
export const DEMO_DATA_NOTE_EN = 'Demo data shown';
export const DEMO_DATA_NOTE_NL = 'Demodata getoond';

// "What we build" — the services Kaizen Kanri sells. Written as
// outcomes the customer recognises from their own week, not as
// product-page bullet points.
export const services = [
  {
    icon: Code2,
    title: 'Custom operations software',
    description: 'Tools shaped around how your team already works. Web-based, mobile-friendly, no licences-per-seat lock-in.',
  },
  {
    icon: Network,
    title: 'Logistics & planning platforms',
    description: 'Inbound, outbound, shifts, KPIs — connected views that replace the spreadsheet pile your shift leads keep emailing each other.',
  },
  {
    icon: Database,
    title: 'Spreadsheets to real systems',
    description: 'When the master file is too big to open and one wrong cell breaks payroll, it is time. We migrate the logic without losing the history.',
  },
  {
    icon: Settings2,
    title: 'Process optimisation',
    description: 'Lean and Kaizen applied where it matters: on the floor, with the people doing the work. No theatre, no posters.',
  },
  {
    icon: LineChart,
    title: 'Dashboards & live collaboration',
    description: 'Numbers your team can act on, refreshed in real time, with sensible defaults so a new hire can read them on day one.',
  },
  {
    icon: Workflow,
    title: 'Workflow design',
    description: 'Validations, approvals, handovers — the boring parts that decide whether your operation runs quietly or noisily.',
  },
];

// "Where we work" — the broad shapes of operation we usually meet.
// Kept as a wide net so visitors recognise themselves; the conversation
// happens once they reach the contact box.
export const industries = [
  { icon: Truck, name: 'Logistics & supply chain' },
  { icon: Factory, name: 'Warehousing & operations' },
  { icon: HeartPulse, name: 'Healthcare & services' },
  { icon: Store, name: 'Retail & wholesale' },
];

// "Why teams pick us" — short, specific, no marketing fluff.
export const reasons = [
  'Tailored to your operation — no off-the-shelf compromises',
  '17+ years of logistics, sales and management behind every build',
  'You speak to the person building the software, not a queue',
  'Built for the floor, validated by the floor',
  'Fixed-scope pilots — you see results before you scale',
  'Excel and PDF exports stay first-class citizens',
  'Your data lives in the EU, on infrastructure you can audit',
  'Existing tools (SAP, payroll, scanners) plug in, not get replaced',
];
